import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate, Link } from 'react-router-dom';
import { useCourseProgress } from '../contexts/CourseProgressContext';
import { 
  ArrowLeftIcon, 
  CheckCircleIcon, 
  XCircleIcon, 
  ClockIcon,
  DocumentTextIcon,
  ChartBarIcon,
  AcademicCapIcon,
  PlayIcon
} from '@heroicons/react/24/outline';

const PreTestResultPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { identifier } = useParams();
  const { 
    markModuleAsCompleted, 
    modules: contextModules,
    fetchCourseProgressAndModules, 
    currentCourseId,
    isModuleCompleted,
    getPreTestModule 
  } = useCourseProgress();

  const [markingError, setMarkingError] = useState('');

  const { results, score, totalQuestions, percentage, courseTitle, moduleTitle, questions: allQuestions } = location.state || {};

  useEffect(() => {
    if (identifier && (!contextModules || contextModules.length === 0 || identifier !== String(currentCourseId))) {
      fetchCourseProgressAndModules(identifier);
    }
  }, [identifier, contextModules, currentCourseId, fetchCourseProgressAndModules]);

  useEffect(() => {
    const markPreTestAsDone = async () => {
      if (results && contextModules && contextModules.length > 0) {
        const preTestModule = getPreTestModule(); 

        if (preTestModule && !isModuleCompleted(preTestModule.id)) {
          try {
            setMarkingError(''); 
            await markModuleAsCompleted(preTestModule.id);
          } catch (error) {
            console.error('Error marking pre-test as completed:', error);
            setMarkingError('Terjadi kesalahan saat menyimpan progres pre-test Anda. Anda tetap dapat melanjutkan.');
          }
        }
      }
    };

    markPreTestAsDone();
  }, [results, contextModules, getPreTestModule, isModuleCompleted, markModuleAsCompleted, identifier]);

  if (!results || !allQuestions) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <DocumentTextIcon className="w-8 h-8 text-yellow-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Hasil Pre-Test Tidak Tersedia</h3>
            <p className="text-gray-600 mb-6">Silakan coba kerjakan pre-test terlebih dahulu.</p>
            <button 
              onClick={() => navigate(`/course/${identifier}/pretest`)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
            >
              Kembali ke Pre-Test
            </button>
          </div>
        </div>
      </div>
    );
  }

  const date = new Date().toLocaleString('id-ID', { dateStyle: 'long', timeStyle: 'short' });

  const handleContinueToMaterial = () => {
    if (!contextModules || contextModules.length === 0) {
      navigate(`/course/${identifier}`);
      return;
    }
  
    const preTestModule = getPreTestModule();
    let startIndex = 0;
    if (preTestModule) {
      const preTestIndex = contextModules.findIndex(m => m.id === preTestModule.id);
      if (preTestIndex !== -1) {
        startIndex = preTestIndex + 1;
      }
    }
  
    let firstActualContentModuleOrder = -1;

    for (let i = startIndex; i < contextModules.length; i++) {
      const module = contextModules[i];
      if (['PAGE', 'pdf', 'web', 'VIDEO', 'text', 'video'].includes(module.type)) {
        firstActualContentModuleOrder = i + 1;
        break;
      }
    }
  
    if (firstActualContentModuleOrder !== -1) {
      navigate(`/course/${identifier}/content/${firstActualContentModuleOrder}`); 
    } else {
      const postTestModule = contextModules.find(m => m.type === 'POST_TEST_QUIZ' || m.type === 'post_test');
      if (postTestModule) {
        navigate(`/course/${identifier}/posttest`); 
      } else {
        navigate(`/course/${identifier}`); 
      }
    }
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600 bg-green-100';
    if (percentage >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreIcon = (percentage) => {
    if (percentage >= 80) return <CheckCircleIcon className="w-6 h-6" />;
    if (percentage >= 60) return <ClockIcon className="w-6 h-6" />;
    return <XCircleIcon className="w-6 h-6" />;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Link
            to={`/course/${identifier}`}
            className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200 mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            <span className="font-medium">Kembali ke Detail Kursus</span>
          </Link>
          
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <AcademicCapIcon className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Hasil Pre-Test</h1>
              <p className="text-lg text-gray-600">{moduleTitle || courseTitle || `Kursus ${identifier}`}</p>
            </div>
          </div>
        </div>

        {markingError && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <div className="flex items-center">
              <div className="w-5 h-5 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                <svg className="w-3 h-3 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <p className="text-yellow-700">{markingError}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Score Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 sticky top-8">
              <div className="text-center mb-6">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${getScoreColor(percentage)}`}>
                  {getScoreIcon(percentage)}
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  {score} / {totalQuestions}
                </h2>
                <p className={`text-lg font-semibold ${getScoreColor(percentage)}`}>
                  {percentage !== undefined && percentage !== null ? percentage.toFixed(0) : 0}%
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Tanggal Ujian:</span>
                  <span className="font-medium text-gray-800">{date}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Total Soal:</span>
                  <span className="font-medium text-gray-800">{totalQuestions}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Jawaban Benar:</span>
                  <span className="font-medium text-green-600">{score}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Jawaban Salah:</span>
                  <span className="font-medium text-red-600">{totalQuestions - score}</span>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start">
                  <ChartBarIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-blue-700">
                    Ini adalah skor awal Anda. Gunakan hasil ini untuk fokus pada area yang perlu ditingkatkan selama kursus.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Questions Review */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                <DocumentTextIcon className="w-6 h-6 mr-2 text-blue-600" />
                Rincian Jawaban
              </h2>
              
              <div className="space-y-6 max-h-[600px] overflow-y-auto pr-2">
                {results.map((item, index) => {
                  const questionDetail = allQuestions.find(q => q.id === item.questionId);
                  const explanation = questionDetail ? questionDetail.explanation : null;

                  return (
                    <div key={item.questionId} className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow duration-200">
                      <div className="flex items-start space-x-3 mb-4">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold text-white flex-shrink-0
                          ${item.isCorrect ? 'bg-green-500' : 'bg-red-500'}`}>
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-800 mb-3 leading-relaxed">
                            {item.teksSoal}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        {item.options.map(opt => (
                          <div 
                            key={opt.id} 
                            className={`p-3 rounded-lg border-2 transition-colors duration-200
                              ${opt.id === item.selectedOptionId && item.isCorrect ? 'bg-green-50 border-green-300' : ''}
                              ${opt.id === item.selectedOptionId && !item.isCorrect ? 'bg-red-50 border-red-300' : ''}
                              ${opt.id === item.correctOptionId && !item.isCorrect ? 'bg-green-50 border-green-300' : ''}
                              ${!opt.id === item.selectedOptionId && !opt.id === item.correctOptionId ? 'bg-gray-50 border-gray-200' : ''}
                            `}
                          >
                            <div className="flex items-center">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-semibold mr-3
                                ${opt.id === item.selectedOptionId && item.isCorrect ? 'bg-green-500 text-white' : ''}
                                ${opt.id === item.selectedOptionId && !item.isCorrect ? 'bg-red-500 text-white' : ''}
                                ${opt.id === item.correctOptionId && !item.isCorrect ? 'bg-green-500 text-white' : ''}
                                ${!opt.id === item.selectedOptionId && !opt.id === item.correctOptionId ? 'bg-gray-300 text-gray-600' : ''}
                              `}>
                                {String.fromCharCode(65 + item.options.indexOf(opt))}
                              </span>
                              <span className={`flex-1 ${opt.id === item.selectedOptionId ? 'font-semibold' : ''}`}>
                                {opt.text}
                              </span>
                              {opt.id === item.selectedOptionId && item.isCorrect && (
                                <CheckCircleIcon className="w-5 h-5 text-green-600 ml-2" />
                              )}
                              {opt.id === item.selectedOptionId && !item.isCorrect && (
                                <XCircleIcon className="w-5 h-5 text-red-600 ml-2" />
                              )}
                              {opt.id === item.correctOptionId && !item.isCorrect && (
                                <CheckCircleIcon className="w-5 h-5 text-green-600 ml-2" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {explanation && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-start">
                            <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                              <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                              </svg>
                            </div>
                            <div>
                              <p className="font-semibold text-blue-800 mb-1">Penjelasan:</p>
                              <p className="text-blue-700 text-sm">{explanation}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {!item.selectedOptionId && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                            <span className="text-yellow-700 text-sm font-medium">Tidak dijawab</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        
        {/* Continue Button */}
        <div className="text-center mt-8">
          <button 
            onClick={handleContinueToMaterial}
            className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            <PlayIcon className="w-5 h-5 mr-2" />
            Lanjutkan ke Materi Kursus
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreTestResultPage;
